"use client";

import {
	RICH_TEXT_FONT_OPTIONS,
	getRichTextFontOptionById,
	matchRichTextFontFamilyToOptionId,
} from "@/fontFamily";
import type { Editor } from "@tiptap/react";
import {
	Bold,
	CaseSensitive,
	Code2,
	Eye,
	Heading1,
	Heading2,
	Heading3,
	Heading4,
	Highlighter,
	Image as ImageIcon,
	Italic,
	Link as LinkIcon,
	List,
	ListOrdered,
	Minus,
	Moon,
	Palette,
	Quote,
	Redo,
	Strikethrough,
	Sun,
	Table as TableIcon,
	Type,
	Underline as UnderlineIcon,
	Undo,
	X,
} from "lucide-react";
import React, { useRef } from "react";
import { FONT_SIZE_OPTIONS, extractNumericValue } from "../utils/styleUtils";
import { ToolbarButton, ToolbarDivider, ToolbarGroup } from "./ToolbarButton";

/** When the caret is inside a paragraph/heading with no range selected, select the whole block (same as font size). */
function expandSelectionToCurrentTextBlock(editor: Editor) {
	const { state } = editor;
	const { selection } = state;
	const { $from } = selection;
	if (!selection.empty) return;
	for (let d = $from.depth; d >= 0; d--) {
		const node = $from.node(d);
		if (node.type.name === "paragraph" || node.type.name.startsWith("heading")) {
			const start = $from.before(d);
			const end = $from.after(d);
			editor.chain().focus().setTextSelection({ from: start, to: end });
			break;
		}
	}
}

interface MainToolbarProps {
	editor: Editor | null;
	showHtml: boolean;
	isDarkMode: boolean;
	onToggleView: (e: React.MouseEvent<HTMLButtonElement>) => void;
	onToggleTheme: (e: React.MouseEvent<HTMLButtonElement>) => void;
	onSetLink: () => void;
	onTriggerFileInput: (e: React.MouseEvent<HTMLButtonElement>) => void;
	onColorChange: (color: string) => void;
	onHighlightChange: (color: string) => void;
	onShowTableModal: () => void;
	onShowHorizontalRuleModal: () => void;
	onClearColor: () => void;
	onClearHighlight: () => void;
}

export const MainToolbar: React.FC<MainToolbarProps> = ({
	editor,
	showHtml,
	isDarkMode,
	onToggleView,
	onToggleTheme,
	onSetLink,
	onTriggerFileInput,
	onColorChange,
	onHighlightChange,
	onShowTableModal,
	onShowHorizontalRuleModal,
	onClearColor,
	onClearHighlight,
}) => {
	const colorInputRef = useRef<HTMLInputElement>(null);
	const highlightInputRef = useRef<HTMLInputElement>(null);

	const runCommand = (e: React.MouseEvent<HTMLButtonElement>, command: () => void) => {
		e.stopPropagation();
		command();
	};

	const triggerColorInput = (e: React.MouseEvent<HTMLButtonElement>) => {
		e.stopPropagation();
		colorInputRef.current?.click();
	};

	const triggerHighlightInput = (e: React.MouseEvent<HTMLButtonElement>) => {
		e.stopPropagation();
		highlightInputRef.current?.click();
	};

	return (
		<div
			onClick={(e) => e.stopPropagation()}
			className="flex flex-wrap items-center gap-2 p-3 bg-gradient-to-r from-gray-50 to-white border-b border-gray-200"
		>
			{/* Undo/Redo */}
			<ToolbarGroup>
				<ToolbarButton
					onClick={(e) => runCommand(e, () => editor?.chain().focus().undo().run())}
					disabled={!editor?.can().undo()}
					icon={Undo}
					label="Undo"
				/>
				<ToolbarButton
					onClick={(e) => runCommand(e, () => editor?.chain().focus().redo().run())}
					disabled={!editor?.can().redo()}
					icon={Redo}
					label="Redo"
				/>
			</ToolbarGroup>

			<ToolbarDivider />

			{/* Text Formatting */}
			<ToolbarGroup>
				<ToolbarButton
					onClick={(e) => runCommand(e, () => editor?.chain().focus().toggleBold().run())}
					isActive={editor?.isActive("bold")}
					icon={Bold}
					label="Bold"
				/>
				<ToolbarButton
					onClick={(e) => runCommand(e, () => editor?.chain().focus().toggleItalic().run())}
					isActive={editor?.isActive("italic")}
					icon={Italic}
					label="Italic"
				/>
				<ToolbarButton
					onClick={(e) => runCommand(e, () => editor?.chain().focus().toggleUnderline().run())}
					isActive={editor?.isActive("underline")}
					icon={UnderlineIcon}
					label="Underline"
				/>
				<ToolbarButton
					onClick={(e) => runCommand(e, () => editor?.chain().focus().toggleStrike().run())}
					isActive={editor?.isActive("strike")}
					icon={Strikethrough}
					label="Strikethrough"
				/>
			</ToolbarGroup>

			<ToolbarDivider />

			{/* Headings */}
			<ToolbarGroup>
				<ToolbarButton
					onClick={(e) => runCommand(e, () => editor?.chain().focus().toggleHeading({ level: 1 }).run())}
					isActive={editor?.isActive("heading", { level: 1 })}
					icon={Heading1}
					label="Heading 1"
				/>
				<ToolbarButton
					onClick={(e) => runCommand(e, () => editor?.chain().focus().toggleHeading({ level: 2 }).run())}
					isActive={editor?.isActive("heading", { level: 2 })}
					icon={Heading2}
					label="Heading 2"
				/>
				<ToolbarButton
					onClick={(e) => runCommand(e, () => editor?.chain().focus().toggleHeading({ level: 3 }).run())}
					isActive={editor?.isActive("heading", { level: 3 })}
					icon={Heading3}
					label="Heading 3"
				/>
				<ToolbarButton
					onClick={(e) => runCommand(e, () => editor?.chain().focus().toggleHeading({ level: 4 }).run())}
					isActive={editor?.isActive("heading", { level: 4 })}
					icon={Heading4}
					label="Heading 4"
				/>
			</ToolbarGroup>

			{/* Font size – applies to paragraph and headings (h1–h4) */}
			<ToolbarGroup>
				<div className="flex items-center gap-1.5">
					<Type className="w-4 h-4 text-gray-500 shrink-0" aria-hidden />
					<select
						value={extractNumericValue(
							editor?.getAttributes("textStyle")?.fontSize as string | undefined
						)}
						onChange={(e) => {
							const size = e.target.value;
							if (!editor) return;
							editor.chain().focus();
							expandSelectionToCurrentTextBlock(editor);
							if (size === "") {
								editor.chain().focus().unsetFontSize().run();
							} else {
								editor.chain().focus().setFontSize(`${size}px`).run();
							}
						}}
						className="border border-gray-300 rounded-md text-sm font-medium bg-white hover:bg-gray-50 py-1.5 pl-2 pr-6 focus:outline-none focus:ring-2 focus:ring-primary-main/20 focus:border-primary-main min-w-[4.5rem]"
						title="Font size"
						aria-label="Font size"
					>
						{FONT_SIZE_OPTIONS.map((opt) => (
							<option key={opt.value || "default"} value={opt.value}>
								{opt.label}
							</option>
						))}
					</select>
				</div>
			</ToolbarGroup>

			<ToolbarGroup>
				<div className="flex items-center gap-1.5">
					<CaseSensitive className="w-4 h-4 text-gray-500 shrink-0" aria-hidden />
					<select
						value={matchRichTextFontFamilyToOptionId(
							editor?.getAttributes("textStyle")?.fontFamily as string | undefined
						)}
						onChange={(e) => {
							const id = e.target.value;
							if (!editor) return;
							editor.chain().focus();
							expandSelectionToCurrentTextBlock(editor);
							if (id === "") {
								editor
									.chain()
									.focus()
									.setMark("textStyle", { fontFamily: null })
									.removeEmptyTextStyle()
									.run();
							} else {
								const opt = getRichTextFontOptionById(id);
								if (opt?.cssValue) {
									editor.chain().focus().setMark("textStyle", { fontFamily: opt.cssValue }).run();
								}
							}
						}}
						className="border border-gray-300 rounded-md text-sm font-medium bg-white hover:bg-gray-50 py-1.5 pl-2 pr-6 focus:outline-none focus:ring-2 focus:ring-primary-main/20 focus:border-primary-main min-w-[9rem] max-w-[12rem]"
						title="Font family"
						aria-label="Font family"
					>
						{RICH_TEXT_FONT_OPTIONS.map((opt) => (
							<option key={opt.id || "default"} value={opt.id}>
								{opt.label}
							</option>
						))}
					</select>
				</div>
			</ToolbarGroup>

			<ToolbarDivider />

			{/* Lists */}
			<ToolbarGroup>
				<ToolbarButton
					onClick={(e) => runCommand(e, () => editor?.chain().focus().toggleBulletList().run())}
					isActive={editor?.isActive("bulletList")}
					icon={List}
					label="Bullet List"
				/>
				<ToolbarButton
					onClick={(e) => runCommand(e, () => editor?.chain().focus().toggleOrderedList().run())}
					isActive={editor?.isActive("orderedList")}
					icon={ListOrdered}
					label="Numbered List"
				/>
			</ToolbarGroup>

			<ToolbarDivider />

			{/* Special Formatting */}
			<ToolbarGroup>
				<ToolbarButton
					onClick={(e) => runCommand(e, () => editor?.chain().focus().toggleBlockquote().run())}
					isActive={editor?.isActive("blockquote")}
					icon={Quote}
					label="Quote"
				/>
				<ToolbarButton
					onClick={(e) => {
						e.stopPropagation();
						onShowHorizontalRuleModal();
					}}
					icon={Minus}
					label="Horizontal Rule"
				/>
			</ToolbarGroup>

			<ToolbarDivider />

			{/* Links & Media */}
			<ToolbarGroup>
				<ToolbarButton onClick={onSetLink} isActive={editor?.isActive("link")} icon={LinkIcon} label="Link" />
				<ToolbarButton onClick={onTriggerFileInput} icon={ImageIcon} label="Insert Image/PDF" />
			</ToolbarGroup>

			<ToolbarDivider />

			{/* Colors & Highlight */}
			<ToolbarGroup>
				<input
					ref={colorInputRef}
					type="color"
					onChange={(e) => onColorChange(e.target.value)}
					style={{
						position: "fixed",
						top: 50,
						left: 50,
						width: 0,
						height: 0,
						opacity: 0,
						pointerEvents: "none",
					}}
					value={editor?.getAttributes("textStyle")?.color ?? "#000000"}
				/>
				<ToolbarButton onClick={triggerColorInput} icon={Palette} label="Text Color" />
				<ToolbarButton
					onClick={(e) => runCommand(e, onClearColor)}
					icon={X}
					label="Clear Text Color"
				/>
				<input
					ref={highlightInputRef}
					type="color"
					onChange={(e) => onHighlightChange(e.target.value)}
					style={{ display: "none" }}
					value={editor?.getAttributes("highlight")?.color}
				/>
				<ToolbarButton
					onClick={triggerHighlightInput}
					isActive={editor?.isActive("highlight")}
					icon={Highlighter}
					label="Highlight"
				/>
				<ToolbarButton
					onClick={(e) => runCommand(e, onClearHighlight)}
					icon={X}
					label="Clear Highlight"
				/>
			</ToolbarGroup>

			<ToolbarDivider />

			{/* Table */}
			<ToolbarGroup>
				<ToolbarButton
					onClick={(e) => {
						e.stopPropagation();
						onShowTableModal();
					}}
					icon={TableIcon}
					label="Insert Table"
				/>
			</ToolbarGroup>

			<ToolbarDivider />

			{/* Subscript/Superscript */}
			<ToolbarGroup>
				<ToolbarButton
					onClick={(e) => runCommand(e, () => editor?.chain().focus().toggleSubscript().run())}
					isActive={editor?.isActive("subscript")}
					icon={() => <span className="text-xs font-bold">x₂</span>}
					label="Subscript"
				/>
				<ToolbarButton
					onClick={(e) => runCommand(e, () => editor?.chain().focus().toggleSuperscript().run())}
					isActive={editor?.isActive("superscript")}
					icon={() => <span className="text-xs font-bold">x²</span>}
					label="Superscript"
				/>
			</ToolbarGroup>

			{/* Theme & View Toggles */}
			<div className="ml-auto flex items-center gap-1">
				<ToolbarButton
					onClick={onToggleTheme}
					icon={isDarkMode ? Sun : Moon}
					label={isDarkMode ? "Switch to Light Background" : "Switch to Dark Background"}
				/>
				<ToolbarButton
					onClick={onToggleView}
					icon={showHtml ? Eye : Code2}
					label={showHtml ? "Switch to Editor" : "Switch to HTML"}
					className="bg-gray-700 hover:bg-gray-800 text-white border-gray-700"
				/>
			</div>
		</div>
	);
};


